import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import axios from 'axios';
import toast from 'react-hot-toast';

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: '#424770',
      fontFamily: 'Inter, system-ui, sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
    invalid: {
      color: '#9e2146',
      iconColor: '#9e2146',
    },
  },
};

const Checkout = () => {
  const { planId } = useParams();
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  const { user, updateUserSubscription } = useAuth();

  const [plan, setPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [clientSecret, setClientSecret] = useState('');
  const [paymentMethod, setPaymentMethod] = useState(null);

  // Load plan details
  useEffect(() => {
    const fetchPlan = async () => {
      try {
        const response = await axios.get('/payments/plans');
        const selectedPlan = response.data.plans.find(p => 
          p.name.toLowerCase() === planId?.toLowerCase()
        );
        
        if (selectedPlan) {
          setPlan(selectedPlan);
        } else {
          toast.error('Plan not found');
          navigate('/pricing');
        }
      } catch (error) {
        console.error('Error fetching plan:', error);
        toast.error('Error loading plan details');
        navigate('/pricing');
      }
    };

    if (planId) {
      fetchPlan();
    }
  }, [planId, navigate]);

  // Create payment intent when plan and billing cycle are selected
  useEffect(() => {
    if (plan && billingCycle) {
      createPaymentIntent();
    }
  }, [plan, billingCycle]);

  const createPaymentIntent = async () => {
    try {
      setLoading(true);
      const amount = plan.price[billingCycle];
      
      const response = await axios.post('/payments/create-payment-intent', {
        amount,
        planId: plan._id,
        billingCycle
      });

      setClientSecret(response.data.clientSecret);
    } catch (error) {
      console.error('Error creating payment intent:', error);
      toast.error('Error setting up payment');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);

    const card = elements.getElement(CardElement);

    // Create payment method
    const { error: methodError, paymentMethod: method } = await stripe.createPaymentMethod({
      type: 'card',
      card: card,
      billing_details: {
        name: `${user.firstName} ${user.lastName}`,
        email: user.email,
      },
    });

    if (methodError) {
      console.error('Payment method error:', methodError);
      toast.error(methodError.message);
      setProcessing(false);
      return;
    }

    setPaymentMethod(method);

    try {
      // Create subscription
      const response = await axios.post('/payments/create-subscription', {
        planId: plan._id,
        billingCycle,
        paymentMethodId: method.id
      });

      if (response.data.clientSecret) {
        // Confirm payment if required
        const { error: confirmError } = await stripe.confirmCardPayment(
          response.data.clientSecret
        );

        if (confirmError) {
          toast.error(confirmError.message);
          setProcessing(false);
          return;
        }
      }

      // Update user subscription in context
      updateUserSubscription({
        plan: plan.name,
        status: 'active',
        stripeSubscriptionId: response.data.subscriptionId
      });

      toast.success(`Successfully subscribed to ${plan.displayName} plan!`);
      navigate('/dashboard');

    } catch (error) {
      console.error('Subscription error:', error);
      toast.error(error.response?.data?.message || 'Error creating subscription');
    } finally {
      setProcessing(false);
    }
  };

  if (!plan) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const currentPrice = plan.price[billingCycle];
  const savings = billingCycle === 'yearly' 
    ? (plan.price.monthly * 12 - plan.price.yearly)
    : 0;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container-custom max-w-4xl">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Complete Your Subscription
          </h1>
          <p className="text-gray-600">
            Subscribe to {plan.displayName} plan and start trading smarter
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Plan Summary */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-semibold">Order Summary</h3>
            </div>
            <div className="card-body">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h4 className="font-semibold text-lg">{plan.displayName} Plan</h4>
                  <p className="text-gray-600 text-sm">{plan.description}</p>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold">
                    ${currentPrice.toFixed(2)}
                  </div>
                  <div className="text-sm text-gray-500">
                    per {billingCycle === 'monthly' ? 'month' : 'year'}
                  </div>
                </div>
              </div>

              {/* Billing Cycle Selection */}
              <div className="mb-6">
                <label className="form-label">Billing Cycle</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setBillingCycle('monthly')}
                    className={`p-3 border rounded-md text-sm font-medium transition-colors ${
                      billingCycle === 'monthly'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    Monthly
                    <div className="text-xs text-gray-500">
                      ${plan.price.monthly.toFixed(2)}/month
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setBillingCycle('yearly')}
                    className={`p-3 border rounded-md text-sm font-medium transition-colors relative ${
                      billingCycle === 'yearly'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    {savings > 0 && (
                      <div className="absolute -top-2 -right-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full">
                        Save ${savings.toFixed(0)}
                      </div>
                    )}
                    Yearly
                    <div className="text-xs text-gray-500">
                      ${plan.price.yearly.toFixed(2)}/year
                    </div>
                  </button>
                </div>
              </div>

              {/* Features List */}
              <div className="border-t pt-4">
                <h5 className="font-medium mb-3">What's included:</h5>
                <ul className="space-y-2">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start text-sm">
                      <svg className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span>{feature.name}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Payment Form */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-semibold">Payment Information</h3>
            </div>
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                {/* Customer Info */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="form-label">First Name</label>
                    <input
                      type="text"
                      className="form-input"
                      value={user.firstName}
                      readOnly
                    />
                  </div>
                  <div>
                    <label className="form-label">Last Name</label>
                    <input
                      type="text"
                      className="form-input"
                      value={user.lastName}
                      readOnly
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    className="form-input"
                    value={user.email}
                    readOnly
                  />
                </div>

                {/* Card Element */}
                <div className="mb-6">
                  <label className="form-label">Card Information</label>
                  <div className="form-input">
                    <CardElement options={CARD_ELEMENT_OPTIONS} />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Your payment information is secure and encrypted.
                  </p>
                </div>

                {/* Terms */}
                <div className="mb-6">
                  <label className="flex items-start">
                    <input
                      type="checkbox"
                      className="mt-1 mr-2"
                      required
                    />
                    <span className="text-sm text-gray-600">
                      I agree to the{' '}
                      <a href="/terms" className="text-blue-600 hover:text-blue-500">
                        Terms of Service
                      </a>{' '}
                      and{' '}
                      <a href="/privacy" className="text-blue-600 hover:text-blue-500">
                        Privacy Policy
                      </a>
                    </span>
                  </label>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={!stripe || processing || loading}
                  className="btn-primary w-full flex items-center justify-center"
                >
                  {processing ? (
                    <>
                      <LoadingSpinner size="sm" className="mr-2" />
                      Processing...
                    </>
                  ) : (
                    <>
                      Subscribe for ${currentPrice.toFixed(2)}/{billingCycle === 'monthly' ? 'month' : 'year'}
                    </>
                  )}
                </button>

                <p className="text-xs text-gray-500 text-center mt-4">
                  You can cancel your subscription at any time from your account settings.
                </p>
              </form>
            </div>
          </div>
        </div>

        {/* Security Notice */}
        <div className="text-center mt-8">
          <div className="inline-flex items-center text-sm text-gray-500">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            Secured by Stripe. Your payment information is encrypted and secure.
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
